import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getAnalytics } from "../../service/api/orders";

const useWindowWidth = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handler = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return width;
};

const TABS = [
    { key: 'wait_at_restaurant', label: 'Wait at Restaurant', shortLabel: 'Wait' },
    { key: 'assign_to_arrived',  label: 'Assign → Arrived',   shortLabel: 'Assign→Arrive' },
    { key: 'pickup_to_delivery', label: 'Pickup → Delivery',  shortLabel: 'Pickup→Del' },
    { key: 'delivery_assigned',  label: 'Delivery Assigned',  shortLabel: 'Assigned' },
    { key: 'delivery_accepted',  label: 'Delivery Accepted',  shortLabel: 'Accepted' },
    { key: 'too_late_orders',    label: 'Too Late Orders',     shortLabel: 'Too Late' },
    { key: 'ready_by_store',     label: 'Ready by Store',      shortLabel: 'Ready' },
    { key: 'cancelled_orders',   label: 'Cancelled Orders',    shortLabel: 'Cancelled' },
];

const SEGMENT_LABELS = {
    placed_to_accept:   'Placed → Accept',
    accept_to_assign:   'Accept → Assign',
    assign_to_arrived:  'Assign → Arrived',
    waiting_restaurant: 'Waiting at Rest.',
    pickup_to_delivery: 'Pickup → Delivery',
};

/* ── Shared atoms ─────────────────────────────────────────────────────────── */

const ViewBtn = ({ uniqueOrderId }) => (
    <a
        href={`/order/${uniqueOrderId}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
            display: 'inline-block',
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#3686ec',
            textDecoration: 'none',
            padding: '4px 10px',
            border: '1px solid rgba(54,134,236,0.25)',
            borderRadius: 6,
            whiteSpace: 'nowrap',
        }}
    >
        View ↗
    </a>
);

const TimeTag = ({ value, highlight }) => (
    <span style={{
        fontWeight: 700,
        fontSize: '0.78rem',
        color: highlight ? '#ef4444' : '#d97706',
        background: highlight ? '#fef2f2' : '#fffbeb',
        padding: '3px 8px',
        borderRadius: 5,
        whiteSpace: 'nowrap',
        border: `1px solid ${highlight ? '#fecaca' : '#fde68a'}`,
    }}>
        {value ?? '—'}
    </span>
);

const ColHeader = ({ children, sticky }) => (
    <th style={{
        padding: '9px 12px',
        textAlign: 'left',
        fontWeight: 700,
        fontSize: '0.68rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#52525b',
        borderBottom: '1px solid #e4e4e7',
        whiteSpace: 'nowrap',
        background: '#f8f8f8',
        ...(sticky ? {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            borderRight: '1px solid #e4e4e7',
        } : {}),
    }}>
        {children}
    </th>
);

const Cell = ({ children, sticky, muted }) => (
    <td style={{
        padding: '10px 12px',
        fontSize: '0.82rem',
        color: muted ? '#71717a' : '#1a1a1a',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        ...(sticky ? {
            position: 'sticky',
            left: 0,
            background: 'inherit',
            zIndex: 1,
            borderRight: '1px solid #f1f5f9',
        } : {}),
    }}>
        {children}
    </td>
);

const OrderId = ({ id }) => (
    <span style={{ fontWeight: 700, color: '#3686ec', fontSize: '0.8rem' }}>
        #{id?.slice(-8)}
    </span>
);

const DelayBar = ({ segments, compact }) => {
    const entries = Object.entries(segments || {}).filter(([, v]) => v);
    const maxSec = Math.max(...entries.map(([, v]) => v.seconds), 1);
    const colors = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3686ec'];

    if (compact) {
        return (
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {entries.map(([key, val], i) => (
                    <span key={key} style={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        color: colors[i % colors.length],
                        background: `${colors[i % colors.length]}18`,
                        padding: '2px 5px',
                        borderRadius: 4,
                    }}>
                        {SEGMENT_LABELS[key]?.split('→')[1]?.trim() ?? key}: {val.display}
                    </span>
                ))}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 160 }}>
            {entries.map(([key, val], i) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ fontSize: '0.65rem', color: '#71717a', width: 100, flexShrink: 0 }}>
                        {SEGMENT_LABELS[key]}
                    </div>
                    <div style={{
                        height: 5,
                        width: `${Math.round((val.seconds / maxSec) * 70)}px`,
                        background: colors[i % colors.length],
                        borderRadius: 3,
                        minWidth: 3,
                        flexShrink: 0,
                    }} />
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: colors[i % colors.length] }}>
                        {val.display}
                    </div>
                </div>
            ))}
        </div>
    );
};

/* ── Mobile card primitives ───────────────────────────────────────────────── */

const MobileCard = ({ children, index }) => (
    <div style={{
        background: index % 2 === 0 ? '#fff' : '#fafafa',
        borderRadius: 10,
        border: '1px solid #e4e4e7',
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    }}>
        {children}
    </div>
);

const MobileRow = ({ label, children }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#71717a', flexShrink: 0 }}>
            {label}
        </span>
        <span style={{ fontSize: '0.82rem', color: '#1a1a1a', textAlign: 'right' }}>
            {children}
        </span>
    </div>
);

/* ── Per-tab table/card components ───────────────────────────────────────── */

const WaitTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Wait Time"><TimeTag value={r.waiting_time} highlight={r.waiting_seconds > 600} /></MobileRow>
                    <MobileRow label="Arrived">{r.arrived_time ?? '—'}</MobileRow>
                    <MobileRow label="Pickup">{r.pickup_time ?? '—'}</MobileRow>
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Wait Time</ColHeader>
                <ColHeader>Arrived</ColHeader>
                <ColHeader>Pickup</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell><TimeTag value={r.waiting_time} highlight={r.waiting_seconds > 600} /></Cell>
                    <Cell muted>{r.arrived_time ?? '—'}</Cell>
                    <Cell muted>{r.pickup_time ?? '—'}</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};

const AssignArrivedTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Delivery Guy">{r.delivery_guy_name}</MobileRow>
                    <MobileRow label="Duration"><TimeTag value={r.duration} highlight={r.duration_seconds > 900} /></MobileRow>
                    <MobileRow label="Assigned">{r.assigned_time ?? '—'}</MobileRow>
                    <MobileRow label="Arrived">{r.arrived_time ?? '—'}</MobileRow>
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Delivery Guy</ColHeader>
                <ColHeader>Duration</ColHeader>
                <ColHeader>Assigned</ColHeader>
                <ColHeader>Arrived</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell>{r.delivery_guy_name}</Cell>
                    <Cell><TimeTag value={r.duration} highlight={r.duration_seconds > 900} /></Cell>
                    <Cell muted>{r.assigned_time ?? '—'}</Cell>
                    <Cell muted>{r.arrived_time ?? '—'}</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};

const PickupDeliveryTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Delivery Guy">{r.delivery_guy_name}</MobileRow>
                    <MobileRow label="Duration"><TimeTag value={r.duration} highlight={r.duration_seconds > 1200} /></MobileRow>
                    <MobileRow label="Pickup">{r.pickup_time ?? '—'}</MobileRow>
                    <MobileRow label="Delivered">{r.delivery_time ?? '—'}</MobileRow>
                    {r.distance && <MobileRow label="Distance">{Number(r.distance)?.toFixed(2)}km</MobileRow>}
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Delivery Guy</ColHeader>
                <ColHeader>Duration</ColHeader>
                <ColHeader>Pickup</ColHeader>
                <ColHeader>Delivered</ColHeader>
                <ColHeader>Distance</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell>{r.delivery_guy_name}</Cell>
                    <Cell><TimeTag value={r.duration} highlight={r.duration_seconds > 1200} /></Cell>
                    <Cell muted>{r.pickup_time ?? '—'}</Cell>
                    <Cell muted>{r.delivery_time ?? '—'}</Cell>
                    <Cell muted>{r.distance ? `${Number(r.distance)?.toFixed(2)}km` : '—'}</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};

const DeliveryAssignedTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Delivery Guy">{r.delivery_guy_name}</MobileRow>
                    <MobileRow label="Duration"><TimeTag value={r.duration} highlight={r.duration_seconds > 600} /></MobileRow>
                    <MobileRow label="Accepted">{r.order_accepted_time ?? '—'}</MobileRow>
                    <MobileRow label="Assigned">{r.assigned_time ?? '—'}</MobileRow>
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Delivery Guy</ColHeader>
                <ColHeader>Duration</ColHeader>
                <ColHeader>Accepted</ColHeader>
                <ColHeader>Assigned</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell>{r.delivery_guy_name}</Cell>
                    <Cell><TimeTag value={r.duration} highlight={r.duration_seconds > 600} /></Cell>
                    <Cell muted>{r.order_accepted_time ?? '—'}</Cell>
                    <Cell muted>{r.assigned_time ?? '—'}</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};

const DeliveryAcceptedTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Delivery Guy">{r.delivery_guy_name}</MobileRow>
                    <MobileRow label="Duration"><TimeTag value={r.duration} highlight={r.duration_seconds > 600} /></MobileRow>
                    <MobileRow label="Store Accepted">{r.order_accepted_time ?? '—'}</MobileRow>
                    <MobileRow label="Del. Accepted">{r.accepted_time ?? '—'}</MobileRow>
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Delivery Guy</ColHeader>
                <ColHeader>Duration</ColHeader>
                <ColHeader>Store Accepted</ColHeader>
                <ColHeader>Del. Accepted</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell>{r.delivery_guy_name}</Cell>
                    <Cell><TimeTag value={r.duration} highlight={r.duration_seconds > 600} /></Cell>
                    <Cell muted>{r.order_accepted_time ?? '—'}</Cell>
                    <Cell muted>{r.accepted_time ?? '—'}</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};

const TooLateTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Delivery Guy">{r.delivery_guy_name}</MobileRow>
                    <MobileRow label="Total Time"><TimeTag value={r.total_time} highlight /></MobileRow>
                    {r.distance && <MobileRow label="Distance">{Number(r.distance)?.toFixed(2)}km</MobileRow>}
                    <MobileRow label="Most Delay">
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 5 }}>
                            {SEGMENT_LABELS[r.most_delay_at] ?? r.most_delay_at ?? '—'}
                        </span>
                    </MobileRow>
                    <div>
                        <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#71717a', marginBottom: 6 }}>Breakdown</div>
                        <DelayBar segments={r.segments} compact />
                    </div>
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Delivery Guy</ColHeader>
                <ColHeader>Total Time</ColHeader>
                <ColHeader>Most Delay At</ColHeader>
                <ColHeader>Breakdown</ColHeader>
                <ColHeader>Distance</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell>{r.delivery_guy_name}</Cell>
                    <Cell><TimeTag value={r.total_time} highlight /></Cell>
                    <Cell>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 5 }}>
                            {SEGMENT_LABELS[r.most_delay_at] ?? r.most_delay_at ?? '—'}
                        </span>
                    </Cell>
                    <Cell><DelayBar segments={r.segments} /></Cell>
                    <Cell muted>{Number(r.distance)?.toFixed(2)}km</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};

const ReadyByStoreTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Time to Ready"><TimeTag value={r.time_to_ready} highlight={r.duration_seconds > 1200} /></MobileRow>
                    <MobileRow label="Store Accepted">{r.store_accepted_time ?? '—'}</MobileRow>
                    <MobileRow label="Order Prepared">{r.order_prepared_time ?? '—'}</MobileRow>
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Time to Ready</ColHeader>
                <ColHeader>Store Accepted</ColHeader>
                <ColHeader>Order Prepared</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell><TimeTag value={r.time_to_ready} highlight={r.duration_seconds > 1200} /></Cell>
                    <Cell muted>{r.store_accepted_time ?? '—'}</Cell>
                    <Cell muted>{r.order_prepared_time ?? '—'}</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};

const CANCELLED_BY_CONFIG = {
    Admin:    { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    Store:    { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
    Customer: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
};

const CancelledByTag = ({ value }) => {
    const cfg = CANCELLED_BY_CONFIG[value] ?? { bg: '#f4f4f5', color: '#52525b', border: '#e4e4e7' };
    return (
        <span style={{
            fontSize: '0.72rem', fontWeight: 700,
            background: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.border}`,
            padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap',
        }}>
            {value ?? '—'}
        </span>
    );
};

const CancelledOrdersTable = ({ rows, isMobile }) => {
    if (isMobile) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {rows.map((r, i) => (
                <MobileCard key={i} index={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <OrderId id={r.unique_order_id} />
                        <ViewBtn uniqueOrderId={r.unique_order_id} />
                    </div>
                    <MobileRow label="Restaurant">{r.restaurant_name}</MobileRow>
                    <MobileRow label="Cancelled By"><CancelledByTag value={r.cancelled_by} /></MobileRow>
                    <MobileRow label="Reason">
                        <span style={{ fontSize: '0.78rem', color: '#52525b', fontStyle: r.reason === '—' ? 'italic' : 'normal' }}>
                            {r.reason}
                        </span>
                    </MobileRow>
                    <MobileRow label="Placed">{r.order_placed_time ?? '—'}</MobileRow>
                    <MobileRow label="Cancelled">{r.order_cancelled_time ?? '—'}</MobileRow>
                </MobileCard>
            ))}
        </div>
    );
    return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
                <ColHeader sticky>#Order</ColHeader>
                <ColHeader>Restaurant</ColHeader>
                <ColHeader>Cancelled By</ColHeader>
                <ColHeader>Reason</ColHeader>
                <ColHeader>Placed</ColHeader>
                <ColHeader>Cancelled</ColHeader>
                <ColHeader></ColHeader>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <Cell sticky><OrderId id={r.unique_order_id} /></Cell>
                    <Cell>{r.restaurant_name}</Cell>
                    <Cell><CancelledByTag value={r.cancelled_by} /></Cell>
                    <Cell>
                        <span style={{
                            fontSize: '0.78rem',
                            color: r.reason === '—' ? '#a1a1aa' : '#1a1a1a',
                            fontStyle: r.reason === '—' ? 'italic' : 'normal',
                            maxWidth: 200, display: 'inline-block',
                            whiteSpace: 'normal', lineHeight: 1.4,
                        }}>
                            {r.reason}
                        </span>
                    </Cell>
                    <Cell muted>{r.order_placed_time ?? '—'}</Cell>
                    <Cell muted>{r.order_cancelled_time ?? '—'}</Cell>
                    <Cell><ViewBtn uniqueOrderId={r.unique_order_id} /></Cell>
                </tr>
            ))}</tbody>
        </table>
    );
};


const TABLE_MAP = {
    wait_at_restaurant: WaitTable,
    assign_to_arrived:  AssignArrivedTable,
    pickup_to_delivery: PickupDeliveryTable,
    delivery_assigned:  DeliveryAssignedTable,
    delivery_accepted:  DeliveryAcceptedTable,
    too_late_orders:    TooLateTable,
    ready_by_store:     ReadyByStoreTable,
    cancelled_orders:   CancelledOrdersTable,
};

/* ── Main modal ───────────────────────────────────────────────────────────── */

export default function AnalyticsModal({ open, onClose, initialTab = 'wait_at_restaurant' }) {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [data, setData]           = useState({});
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const tabsRef                   = useRef(null);
    const windowWidth               = useWindowWidth();
    const isMobile                  = windowWidth < 640;

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getAnalytics();
            setData(res);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            setActiveTab(initialTab);
            setData({});
            load();
        }
    }, [open, initialTab]);

    // Scroll active tab pill into view on mobile
    useEffect(() => {
        if (tabsRef.current) {
            const activeEl = tabsRef.current.querySelector('[data-active="true"]');
            activeEl?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        }
    }, [activeTab]);

    useEffect(() => {
        const handler = (e) => e.key === 'Escape' && onClose?.();
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    if (!open) return null;

    const rows = (data && activeTab && data[activeTab]) ? data[activeTab] : [];
    const TableComponent = TABLE_MAP[activeTab] ?? null;

    return (
        <div
            onClick={(e) => e.target === e.currentTarget && onClose?.()}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: isMobile ? 'flex-end' : 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: isMobile ? 0 : 16,
            }}
        >
            <div style={{
                background: '#fff',
                borderRadius: isMobile ? '16px 16px 0 0' : 16,
                width: '100%',
                maxWidth: isMobile ? '100%' : 1100,
                maxHeight: isMobile ? '92vh' : '85vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}>

                {/* Drag handle (mobile only) */}
                {isMobile && (
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2, flexShrink: 0 }}>
                        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d4d4d8' }} />
                    </div>
                )}

                {/* ── Tab bar ── */}
                <div style={{ borderBottom: '1px solid #e4e4e7', background: '#fff', flexShrink: 0 }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: isMobile ? '0 8px 0 0' : '0 12px 0 4px',
                    }}>
                        <div
                            ref={tabsRef}
                            style={{
                                display: 'flex',
                                overflowX: 'auto',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                WebkitOverflowScrolling: 'touch',
                                flex: 1,
                            }}
                        >
                            {TABS.map((t) => {
                                const isActive = activeTab === t.key;
                                const count = data?.[t.key]?.length;
                                return (
                                    <button
                                        key={t.key}
                                        data-active={isActive}
                                        onClick={() => setActiveTab(t.key)}
                                        style={{
                                            padding: isMobile ? '10px 11px' : '12px 14px',
                                            fontSize: isMobile ? '0.74rem' : '0.8rem',
                                            fontWeight: isActive ? 700 : 500,
                                            color: isActive ? '#2563eb' : '#71717a',
                                            background: 'transparent',
                                            border: 'none',
                                            borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                                            cursor: 'pointer',
                                            borderRadius: 0,
                                            transition: 'color 0.15s, border-color 0.15s',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 5,
                                        }}
                                    >
                                        {isMobile ? t.shortLabel : t.label}
                                        {count !== undefined && (
                                            <span style={{
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                background: isActive ? '#dbeafe' : '#f4f4f5',
                                                color: isActive ? '#1d4ed8' : '#a1a1aa',
                                                padding: '1px 6px',
                                                borderRadius: 10,
                                                minWidth: 18,
                                                textAlign: 'center',
                                            }}>
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={onClose}
                            style={{
                                background: '#f4f4f5',
                                border: 'none',
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                color: '#52525b',
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginLeft: 6,
                            }}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* ── Body ── */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: isMobile ? 'hidden' : 'auto' }}>
                    {loading && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#71717a', fontSize: '0.9rem', gap: 10 }}>
                            <span style={{
                                display: 'inline-block', width: 16, height: 16,
                                border: '2px solid #e4e4e7', borderTopColor: '#3b82f6',
                                borderRadius: '50%', animation: 'am-spin 0.7s linear infinite',
                            }} />
                            Loading…
                        </div>
                    )}
                    {error && (
                        <div style={{ padding: 24, color: '#ef4444', textAlign: 'center', fontSize: '0.85rem' }}>
                            {error}
                        </div>
                    )}
                    {!loading && !error && rows.length === 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#a1a1aa', fontSize: '0.9rem', flexDirection: 'column', gap: 10 }}>
                            <span style={{ fontSize: '2rem' }}>📭</span>
                            No data for today
                        </div>
                    )}
                    {!loading && !error && rows.length > 0 && TableComponent && (
                        <div style={{ overflowX: isMobile ? 'hidden' : 'auto' }}>
                            <TableComponent rows={rows} isMobile={isMobile} />
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div style={{
                    padding: isMobile ? '10px 16px 22px' : '10px 20px',
                    borderTop: '1px solid #e4e4e7',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                    background: '#fff',
                }}>
                    <span style={{ fontSize: '0.72rem', color: '#a1a1aa' }}>
                        {isMobile ? 'Today · Longest first' : "Today's orders only · Sorted by longest duration first"}
                    </span>
                    <button
                        onClick={load}
                        style={{
                            fontSize: '0.78rem', fontWeight: 700, color: '#2563eb',
                            background: '#eff6ff', border: 'none', padding: '6px 14px',
                            borderRadius: 7, cursor: 'pointer',
                        }}
                    >
                        ↻ Refresh
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes am-spin { to { transform: rotate(360deg); } }
                [data-am-tabs]::-webkit-scrollbar { display: none; }
            `}</style>
        </div>
    );
}